/** @jsxImportSource @emotion/react */
import React, { useState, useRef, useEffect, useMemo } from 'react';
import { CellType, DataMap } from '@grapecity/wijmo.grid';
import {
  InputBox,
  SearchBox,
  SearchBoxRow,
  SearchButtonWrap,
  SearchCols,
  SearchRows,
} from '../../components/layouts/SearchBox';
import { MultiComboBox } from '../../components/selects/ComboBox';
import { useTranslation } from 'react-i18next';
import { Code } from '../../models/common/CommonCode';
import { getCommonCodeNames, getCommonCodeNamesCondition } from '../../apis/admin/CommonCode';
import {
  ControlBtnGroup,
  GlobalBtnGroup,
  SubTitleGroup,
  SubTitleLayout,
} from '../../components/layouts/ContentLayout';
import { Button } from '@mui/material';
import { IconButton } from '../../components/buttons/IconSVG';
import CustomGrid from '../../components/grids/CustomGrid';
import { CrudCode } from '../../models/common/Edit';
import { useMessageBar } from '../../hooks/useMessageBar';
import { CommonUtil } from '../../utils/CommonUtil';
import { GridStatusCellTemplate } from '../../components/grids/GridStatusCellRenderer';
import useEvent from 'react-use-event-hook';
import { hasRole } from '../../utils/SessionUtil';
import { toggleClass } from '@grapecity/wijmo';
import {
  getEquGroupParameter,
  getSetupManagerList,
  saveSetupManagerList,
} from '../../apis/pjm/SetupManager';
import { useCommonModal } from '../../hooks/useCommonModal';
import { SetupManager } from '../../models/pjm/SetupManager';
import { MpMcrcList, SearchParam } from '../../models/mp/MpMcrc';
import UserMultiModal from '../../components/modals/common/UserMultiModal';
import { GatingContListPopUp } from '../../pages/gtng/popup/GatingContListPopUp';
import { getMpMcrcList, saveMpMcrcList } from 'apis/mp/MpMcrc';
import useSessionStore from 'stores/useSessionStore';
import _ from 'lodash';

/**
 * 분과위원관리 페이지
 */

const MpMcrcManagementPage = () => {
  const { t } = useTranslation();
  const { openMessageBar } = useMessageBar();
  const { openCommonModal } = useCommonModal();

  const { mpClsfType } = useSessionStore();
  const { prdnProcCd } = useSessionStore();

  const [mpClsfTypeCode, setMpClsfTypeCode] = useState<Code[]>([]);
  const [prdnProcCode, setPrdnProcCode] = useState<Code[]>([]);

  const [errors, setErrors] = useState<any>({});

  const [hasAuth, setHasAuth] = useState<boolean>(false);

  const [searchParamMpClsfType, setSearchParamMpClsfType] = useState<string>('');
  const [searchParamPrdnProcCd, setSearchParamPrdnProcCd] = useState<string>('');

  const gridRef = useRef<any>();
  const [hitTest, setHitTest] = useState<any>();
  const [flexRef, setFlexRef] = useState<any>();
  const [flexItem, setFlexItem] = useState<any>();

  const [rowData, setRowData] = useState<MpMcrcList[]>([]);
  const [delData, setDelData] = useState<MpMcrcList[]>([]);

  const [isOpenUserMultiModal, setOpenUserMultiModal] = useState<boolean>(false);
  const [isOpenUserReadModal, setOpenUserReadModal] = useState<boolean>(false);

  const [userIds, setUserIds] = useState<string>('');

  const initSearchParam = {
    mpClsfType: '',
    mpClsfTypeCds: [],
    prdnProcCd: '',
    prdnProcCds: [],
  };

  const [searchParam, setSearchParam] = useState<SearchParam>(initSearchParam);

  useEffect(() => {
    initCondition();
  }, []);

  useEffect(() => {
    console.log('prdnProcCd', prdnProcCd);
    console.log('mpClsfType', mpClsfType);
    setSearchParam({
      mpClsfType: _.isEmpty(mpClsfType) ? '' : mpClsfType,
      mpClsfTypeCds: _.isEmpty(mpClsfType) ? [] : [mpClsfType],
      prdnProcCd: _.isEmpty(prdnProcCd) ? '' : prdnProcCd,
      prdnProcCds: _.isEmpty(prdnProcCd) ? [] : [prdnProcCd],
    });
    if (!_.isEmpty(searchParamMpClsfType) || !_.isEmpty(searchParamPrdnProcCd)) {
      btnSearch();
    } else {
      btnSearch();
    }
  }, [searchParamMpClsfType, searchParamPrdnProcCd]);

  const initCondition = async () => {
    const mpClsfTypeCode: Code[] = await getCommonCodeNames('MP_CLSF_CD'); // MP 분류
    const prdnProcCode = await getCommonCodeNamesCondition({
      optValCtn2: 'Y', // MP적용여부
      cmnGrCd: 'PRDN_PROC_CD',
    }); // 공정

    setMpClsfTypeCode(mpClsfTypeCode);
    setPrdnProcCode(prdnProcCode);
    setSearchParamMpClsfType(mpClsfType);
    setSearchParamPrdnProcCd(prdnProcCd);

    if (hasRole('ADM') || hasRole('MP_MANAGER')) {
      setHasAuth(true);
    }
  };

  const btnSearch = () => {
    getMpMcrcList(searchParam).then((result: MpMcrcList[]) => {
      if (result !== null) {
        setRowData(result);
      } else {
        openMessageBar({
          type: 'error',
          content: t(
            'com.msg.요청 정보 조회 중 오류가 발생했습니다.',
            '요청 정보 조회 중 오류가 발생했습니다.'
          ),
        });
      }
    });
  };

  const btnSave = () => {
    const validStatusCorU = rowData.filter(
      (element) => element.crudKey == CrudCode.CREATE || element.crudKey == CrudCode.UPDATE
    );
    const valid = validStatusCorU
      .map((rowNode, index) => {
        if (rowNode.crudKey != CrudCode.DELETE) {
          if (rowNode.mpClsfType == null || rowNode.mpClsfType == '')
            return `${t('mp.label.분류를 선택해 주세요.', '분류를 선택해 주세요.')}`;
          if (rowNode.prdnProcCd == null || rowNode.prdnProcCd == '')
            return `${t('mp.label.공정을 선택해 주세요.', '공정을 선택해 주세요.')}`;
          if (rowNode.mcrcId == null || rowNode.mcrcId == '')
            return `${t('mp.label.분과위원을 선택해 주세요.', '분과위원을 선택해 주세요.')}`;
        }
      })
      .filter((element) => element !== undefined);
    if (valid.length) {
      const content = valid[0]?.toString();
      openMessageBar({ type: 'error', content: content || '' });
      return false;
    }

    // 중복 체크
    openCommonModal({
      modalType: 'confirm',
      content: t('com.label.저장하시겠습니까?', '저장하시겠습니까?'),
      yesCallback: () => {
        const saveRows = rowData
          .map((rowNode) => {
            return rowNode.crudKey ? rowNode : null;
          })
          .filter((element) => element !== null) as SetupManager[];

        const saveRowsData = [...saveRows, ...delData];

        saveMpMcrcList(saveRowsData).then((result) => {
          if (result.deletedRows === -1) {
            openMessageBar({
              type: 'error',
              content: t(
                'com.label.사용중인 분과위원이 존재합니다.',
                '사용중인 분과위원이 존재합니다.'
              ),
            });
            return false;
          }
          if (result.insertedRows === -1 || result.updatedRows === -1) {
            openMessageBar({
              type: 'error',
              content: t(
                'com.label.중복된 MP분류, 공정이 존재합니다.',
                '중복된 MP분류, 공정이 존재합니다.'
              ),
            });
            return false;
          }
          if (!result) {
            openMessageBar({
              type: 'error',
              content: t('com.label.저장 중 오류가 발생했습니다.', '저장 중 오류가 발생했습니다.'),
            });
            return false;
          } else if (result.insertedRows === -1) {
            openMessageBar({
              type: 'error',
              content: t(
                'com.label.MP분류, 공정은 중복 등록할 수 없습니다.',
                '분류, 공정은 중복 등록할 수 없습니다.'
              ),
            });
            return false;
          } else {
            openMessageBar({
              type: 'confirm',
              content: t('com.label.저장되었습니다.', '저장되었습니다.'),
            });
            setDelData([]);
            btnSearch();
          }
        });
      },
      noCallback: () => {
        return false;
      },
    });
  };

  const btnAddRow = () => {
    const newRow = {
      crudKey: CrudCode.CREATE,
      useYn: 'Y',
    } as MpMcrcList;
    setRowData([newRow, ...rowData]);
  };

  const btnDelRow = () => {
    const selectedRowNodes = flexRef.selectedRows;

    const isSelected = selectedRowNodes.filter((item) => item.isSelected);
    const selectedData = isSelected.map(({ dataItem }) => dataItem);

    if (isSelected.length < 1) {
      openMessageBar({
        type: 'error',
        content: t('mp.label.삭제할 행을 선택해 주세요.', '삭제할 행을 선택해 주세요.'),
      });
      return false;
    } else {
      const selectedIds = selectedRowNodes
        .map((rowNode) => {
          return parseInt(rowNode._idx!);
        })
        .reverse();

      selectedIds.forEach((idx) => {
        if (rowData[idx].crudKey === CrudCode.CREATE) {
          delete rowData[idx];
        } else {
          rowData[idx].crudKey = CrudCode.DELETE;
        }
      });

      const filteredData = rowData.filter((element) => element !== undefined);
      setRowData(filteredData);
    }
  };

  // const btnCopyRow = () => {
  //   const selectedRowNodes = flexRef.selectedRows;

  //   const isSelected = selectedRowNodes.filter((item) => item.isSelected);

  //   if (isSelected.length < 1) {
  //     openMessageBar({
  //       type: 'error',
  //       content: t('mp.label.복사할 행을 선택해 주세요.', '복사할 행을 선택해 주세요.'),
  //     });
  //     return false;
  //   } else {
  //     const selectNo = 0; //단일 선택 복사 시 0번째
  //     const copiedData = isSelected[selectNo].dataItem;
  //     const newRow = {
  //       crudKey: CrudCode.CREATE,
  //       mpClsfType: copiedData?.mpClsfType,
  //       mpClsfTypeNm: copiedData?.mpClsfTypeNm,
  //       prdnProcCd: copiedData?.prdnProcCd,
  //       prdnProcCdNm: copiedData?.prdnProcCdNm,
  //       mcrcId: copiedData?.mcrcId,
  //       mcrcNm: copiedData?.mcrcNm,
  //     } as MpMcrcList;
  //     setRowData([newRow, ...rowData]);
  //   }
  // };

  const handleOnChange = (name: string, value: string) => {
    setSearchParam({
      ...searchParam,
      [name]: value,
    });
    setErrors({ ...errors, [name]: false });
  };

  const onInitialized = (grid) => {
    setFlexRef(grid);
    gridRef.current = grid;

    grid.hostElement.addEventListener('click', (e) => {
      const len = grid.rows.length;
      if (len == 0) return;
      const ht = grid.hitTest(e);
      grid.startEditing(true);

      if (ht.row < 0 || ht.col < 0) return;
      if (ht.panel.cellType != 1) return; // 선택된 영역이 셀이 아니면 나가리.

      const item = grid.rows[ht.row].dataItem; // 선택한 row의 정보

      if (e.target instanceof HTMLButtonElement) {
        const col = grid.columns[ht.col]; // 선택한 컬럼의 정보
        const item = grid.rows[ht.row].dataItem; // 선택한 row의 정
        setFlexItem(item);
        setHitTest(ht);
        switch (e.target.id) {
          case 'mcrcId':
            setOpenUserMultiModal(true);
            setUserIds(item.mcrcId);
            break;
          case 'readOpenModal':
            setOpenUserReadModal(true);
            setUserIds(item.mcrcId);
            break;
        }
      }
    });
  };

  const layoutDefinition = useMemo(() => {
    const mpClsfTypeCodeMap = new DataMap(mpClsfTypeCode, 'cmnCd', 'cmnCdNm');
    const prdnProcCodeMap = new DataMap(prdnProcCode, 'cmnCd', 'cmnCdNm');

    return [
      {
        binding: 'crudKey',
        header: String(t('com.label.상태', '상태')),
        isReadOnly: true,
        width: 50,
        align: 'center',
        cellTemplate: GridStatusCellTemplate,
      },
      {
        binding: 'planProcId',
        visible: false,
      },
      {
        binding: 'mpClsfType',
        header: String(t('mp.label.분류', '분류')),
        width: 200,
        align: 'center',
        cssClass: 'WijmoSelect',
        dataMap: mpClsfTypeCodeMap,
        cellTemplate: (item) => {
          if (item.item.crudKey === CrudCode.CREATE) {
            if (mpClsfTypeCodeMap.getDisplayValue(item.value) == undefined) {
              return `<span>${item.text}</span>`;
            } else {
              return `<span>${mpClsfTypeCodeMap.getDisplayValue(item.text)}</span>`;
            }
          } else {
            return `<span>${item.item.mpClsfTypeNm}</span>`;
          }
        },
      },
      {
        binding: 'mpClsfTypeNm',
        visible: false,
      },
      {
        binding: 'prdnProcCd',
        header: String(t('mp.label.공정', '공정')),
        width: 200,
        align: 'center',
        cssClass: 'WijmoSelect',
        dataMap: prdnProcCodeMap,
        cellTemplate: (item) => {
          if (item.item.crudKey === CrudCode.CREATE) {
            if (prdnProcCodeMap.getDisplayValue(item.value) == undefined) {
              return `<span>${item.text}</span>`;
            } else {
              return `<span>${prdnProcCodeMap.getDisplayValue(item.text)}</span>`;
            }
          } else {
            return `<span>${item.item.prdnProcCdNm}</span>`;
          }
        },
      },
      {
        binding: 'prdnProcCdNm',
        visible: false,
      },
      {
        binding: 'mcrcId',
        header: String(t('mp.label.분과위원', '분과위원')),
        width: '*',
        align: 'left',
        isReadOnly: !hasAuth,
        cssClass: 'WijmoFind',
        cellTemplate: (o) => setMcrcId(o.item.crudKey, o.item.mcrcId, o.item.mcrcNm, 'mcrcId'),
      },
      {
        binding: 'mcrcNm',
        visible: false,
      },
      {
        binding: 'dataInsUserNm',
        header: String(t('com.label.작성자', '작성자')),
        width: 150,
        align: 'center',
        isReadOnly: true,
      },
      {
        binding: 'dataInsDtm',
        header: String(t('com.label.작성일자', '작성일자')),
        width: 150,
        align: 'center',
        isReadOnly: true,
      },
      {
        binding: 'dataUpdUserNm',
        header: String(t('com.label.수정자', '수정자')),
        width: 150,
        align: 'center',
        isReadOnly: true,
      },
      {
        binding: 'dataUpdDtm',
        header: String(t('com.label.수정일자', '수정일자')),
        width: 150,
        align: 'center',
        isReadOnly: true,
      },
    ];
  }, [rowData]);

  const setMcrcId = (crudKey, mgrId, mgrNm, mgrBinding) => {
    if (crudKey == CrudCode.CREATE) {
      if (mgrId === null || mgrId === undefined) {
        return `<span></span><button id="${
          hasAuth === true ? `${mgrBinding}` : 'readOpenModal'
        }" />`;
      } else {
        return `<span>${mgrNm}</span><button id="${
          hasAuth === true ? `${mgrBinding}` : 'readOpenModal'
        }" />`;
      }
    } else {
      if (mgrId === null || mgrId === undefined) {
        return `<span></span><button id="${
          hasAuth === true ? `${mgrBinding}` : 'readOpenModal'
        }" />`;
      } else {
        return `<span>${mgrNm}</span><button id="${
          hasAuth === true ? `${mgrBinding}` : 'readOpenModal'
        }" />`;
      }
    }
  };

  const onItemFormatter = useEvent((panel, row, col, cell) => {
    if (CellType.Cell === panel.cellType) {
      const binding = panel.columns[col].binding;
      const item = panel.rows[row].dataItem;

      if (item.crudKey !== CrudCode.CREATE) {
        if (['mpClsfType', 'prdnProcCd'].includes(binding) || !hasAuth) {
          cell.ariaReadOnly = true;
          toggleClass(cell, 'WijmoSelect', !hasAuth);
          if (cell.firstElementChild instanceof HTMLButtonElement) {
            cell.firstChild.remove();
          }
        }
      }
    }
  });

  const beginningEdit = useEvent((grid, e) => {
    const col = grid.columns[e.col];
    const binding = col.binding;
    const item = grid.rows[e.row].dataItem;
    if (item.crudKey === CrudCode.CREATE) {
      if (['mcrcId'].includes(binding) || !hasAuth) {
        e.cancel = true;
      }
    }

    if (item.crudKey !== CrudCode.CREATE) {
      if (['mpClsfType', 'prdnProcCd', 'mcrcId'].includes(binding) || !hasAuth) {
        e.cancel = true;
      }
    }
  });

  const btnReload = () => {
    setSearchParam(
      (prev) =>
        Object.keys(prev).reduce(
          (acc, cur) => Object.assign(acc, { [cur]: initSearchParam[cur] || '' }),
          {}
        ) as SearchParam
    );
  };

  const callbackUserData = (callbackData) => {
    flexItem['mcrcId'] = callbackData.userId;
    flexItem['mcrcNm'] = callbackData.empNm;
    if (flexItem['crudKey'] !== CrudCode.CREATE && flexItem['crudKey'] !== CrudCode.DELETE) {
      flexItem['crudKey'] = CrudCode.UPDATE;
    }
    gridRef.current.refresh();
  };

  return (
    <>
      <SearchBox>
        <SearchBoxRow>
          <InputBox>
            <SearchRows>
              <SearchCols>
                <label>{String(t('mp.label.분류', '분류'))}</label>
                <MultiComboBox
                  id="mpClsfType"
                  options={mpClsfTypeCode}
                  placeholder={String(t('com.label.전체', '전체'))}
                  defaultValue={searchParam?.mpClsfTypeCds || []}
                  onChange={(value) => {
                    handleOnChange('mpClsfType', value.join());
                  }}
                />
              </SearchCols>
              <SearchCols>
                <label>{String(t('mp.label.공정', '공정'))}</label>
                <MultiComboBox
                  id="prdnProcCd"
                  options={prdnProcCode}
                  placeholder={String(t('com.label.전체', '전체'))}
                  defaultValue={searchParam?.prdnProcCds || []}
                  onChange={(value) => {
                    handleOnChange('prdnProcCd', value.join());
                  }}
                />
              </SearchCols>
            </SearchRows>
          </InputBox>
          <SearchButtonWrap>
            <Button
              css={IconButton.button}
              className="reload"
              onClick={btnReload}
              disableRipple
            ></Button>
            <Button
              css={IconButton.button}
              className="find"
              onClick={() => btnSearch()}
              disableRipple
            >
              {t('com.button.조회', '조회')}
            </Button>
          </SearchButtonWrap>
        </SearchBoxRow>
      </SearchBox>
      <SubTitleLayout>
        <SubTitleGroup>
          <h3>{t('mp.label.분과위원 List', '분과위원 List')}</h3>
          <span className="total">
            {t('com.label.총', '총')}
            <span>{rowData?.length ?? 0}</span>
            {t('com.label.건', '건')}
          </span>
        </SubTitleGroup>
        <ControlBtnGroup>
          {hasAuth && (
            <Button css={IconButton.button} className="addRow" onClick={btnAddRow} disableRipple>
              {t('com.button.행추가', '행추가')}
            </Button>
          )}
          {hasAuth && (
            <Button css={IconButton.button} className="delRow" onClick={btnDelRow} disableRipple>
              {t('com.button.행삭제', '행삭제')}
            </Button>
          )}
          {/* {hasAuth && (
            <Button css={IconButton.button} className="copyRow" onClick={btnCopyRow} disableRipple>
              {t('com.button.행복사', '행복사')}
            </Button>
          )} */}
          <Button
            css={IconButton.button}
            className="Exceldown"
            onClick={() => {
              CommonUtil.exportWijmoExcelGrid(
                flexRef,
                t('mp.label.분과위원 List', '분과위원 List'),
                t('mp.label.분과위원 List', '분과위원 List')
              );
            }}
            disableRipple
          >
            {t('com.button.다운로드', '화면 다운로드')}
          </Button>
          <Button css={IconButton.button} className="refresh" onClick={btnSearch} disableRipple>
            {t('com.button.새로고침', '새로고침')}
          </Button>
        </ControlBtnGroup>
      </SubTitleLayout>
      <CustomGrid
        layoutDefinition={layoutDefinition}
        rowData={rowData}
        initialized={onInitialized}
        itemFormatter={onItemFormatter}
        beginningEdit={beginningEdit}
        height={600}
        frozenColumns={4}
        autoCheck={true}
        isFilter={false}
        allowPinning={false}
      />
      <GlobalBtnGroup>
        {hasAuth && (
          <Button css={IconButton.button} className="save" onClick={btnSave}>
            {t('com.button.저장', '저장')}
          </Button>
        )}
      </GlobalBtnGroup>
      {isOpenUserMultiModal && (
        <UserMultiModal
          open={isOpenUserMultiModal}
          close={() => setOpenUserMultiModal(false)}
          title={t('com.label.담당자 조회', '담당자 조회')}
          defaultUserId={userIds}
          singleSelect={false}
          onCallback={(callbackData: any) => {
            if (callbackData.userId === '') {
              openMessageBar({
                type: 'warning',
                content: t('com.msg.선택된 사용자가 없습니다.', '선택된 사용자가 없습니다.'),
              });
              return false;
            }
            callbackUserData(callbackData);
          }}
        />
      )}
      {isOpenUserReadModal && (
        <GatingContListPopUp
          open={isOpenUserReadModal}
          close={() => setOpenUserReadModal(false)}
          title={t('com.label.담당자', '담당자')}
          initParam={userIds}
        />
      )}
    </>
  );
};
export default MpMcrcManagementPage;
